import React, { useState } from 'react';

// Material UI
import Box from '@material-ui/core/Box';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import Typography from '@material-ui/core/Typography';

// Data
import {
  AgeGroupJerseyRetentionRow, buildSetAgeGroupJerseyRetention, getAgeGroupJerseyRetention,
} from '../../lib/services/age-group.service';
import { useCompanySportTerminology } from '../../lib/stores/company.store';
import { useIsMounted } from '../../lib/hooks/use-is-mounted';
import { useSingleFlight } from '../../lib/hooks/use-single-flight';
import { getMessages } from './jersey-assignment.messages';

// Components
import { FieldCaption } from '../_core/_ui/field-caption.component';
import { IntlMsg } from '../_core/IntlMsg/intl-msg.component';

type AgeGroupRetentionChoice = 'FOLLOW' | 'KEEP' | 'RESET';

export const choiceForAgeGroup = (value?: boolean | null): AgeGroupRetentionChoice => {
  if (value === true) return 'KEEP';
  if (value === false) return 'RESET';
  return 'FOLLOW';
};

// FOLLOW clears the age group's choice (null) so it follows its assessment again.
export const valueForChoice = (choice: AgeGroupRetentionChoice): boolean | null => {
  if (choice === 'KEEP') return true;
  if (choice === 'RESET') return false;
  return null;
};

interface IAgeGroupJerseyRetentionSettingsProps {
  assessmentId?: string | null;
  assessmentRetains: boolean;
}

export const AgeGroupJerseyRetentionSettings: React.FC<IAgeGroupJerseyRetentionSettingsProps> = ({ assessmentId, assessmentRetains }) => {
  const messages = getMessages(useCompanySportTerminology());
  const { ageGroups, loading, error } = getAgeGroupJerseyRetention(assessmentId);
  const { setAgeGroupJerseyRetention } = buildSetAgeGroupJerseyRetention();
  const [saveError, setSaveError] = useState<React.ReactNode>(null);
  const isMounted = useIsMounted();

  const saveFlight = useSingleFlight(async (ageGroup: AgeGroupJerseyRetentionRow, choice: AgeGroupRetentionChoice) => {
    setSaveError(null);
    try {
      await setAgeGroupJerseyRetention(ageGroup._id, valueForChoice(choice));
    } catch (err) {
      console.error('setAgeGroupJerseyRetention failed:', err);
      if (isMounted()) setSaveError(<IntlMsg {...messages.errAgeGroupSave} values={{ name: ageGroup.name }} />);
    }
  }, { key: (ageGroup) => ageGroup._id });

  const renderRows = () => {
    if (loading && !ageGroups.length) return <Typography><IntlMsg {...messages.loadingAgeGroups} /></Typography>;
    if (error) return <Typography color="error"><IntlMsg {...messages.errAgeGroupsLoad} /></Typography>;
    if (!ageGroups.length) return <Typography color="textSecondary"><IntlMsg {...messages.noAgeGroups} /></Typography>;

    return ageGroups.map(ageGroup => {
      const labelId = `jersey-retention-${ageGroup._id}`;
      return (
        <Box key={ageGroup._id} display="flex" alignItems="center" justifyContent="space-between" py={1}>
          <Typography variant="subtitle2">{ageGroup.name}</Typography>
          <FormControl variant="outlined" size="small" style={{ minWidth: 260 }}>
            <InputLabel id={labelId}><IntlMsg {...messages.lblAgeGroupRetention} /></InputLabel>
            <Select
              labelId={labelId}
              label={<IntlMsg {...messages.lblAgeGroupRetention} />}
              value={choiceForAgeGroup(ageGroup.retainJerseyAssignments)}
              disabled={saveFlight.isInFlight(ageGroup._id)}
              onChange={event => saveFlight.run(ageGroup, event.target.value as AgeGroupRetentionChoice)}
            >
              <MenuItem value="FOLLOW">
                <IntlMsg {...messages.optFollowAssessment} values={{
                  current: <IntlMsg {...(assessmentRetains ? messages.currentKept : messages.currentReset)} />,
                }} />
              </MenuItem>
              <MenuItem value="KEEP"><IntlMsg {...messages.optKeep} /></MenuItem>
              <MenuItem value="RESET"><IntlMsg {...messages.optReset} /></MenuItem>
            </Select>
          </FormControl>
        </Box>
      );
    });
  };

  return (
    <Box mt={3}>
      <Typography variant="subtitle1"><IntlMsg {...messages.titleAgeGroups} /></Typography>
      <FieldCaption><IntlMsg {...messages.captionAgeGroups} /></FieldCaption>
      {saveError && <Typography variant="body2" color="error">{saveError}</Typography>}
      {renderRows()}
    </Box>
  );
};
